// 📄 pages/SAP-BTP/IntegrationSuiteHome.tsx
// Vista dedicada de Integration Suite, accedida desde la tarjeta homónima
// en BTPHome.tsx. Mismo patrón de navegación por estado (`page`, ver
// App.js/OperativeApp) que el resto de sub-vistas de Ariba/BTP — no es una
// ruta de React Router real, porque ninguna de esas sub-vistas lo es.
import React from 'react';
import { Workflow, FolderGit2, Activity, Sliders, Plus } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';

// En PRD solo se muestran las herramientas que ya funcionan (mismo criterio
// que HomeLanding.tsx / BTPHome.tsx); en QAS, también las que están en desarrollo.
const MOSTRAR_MODULOS_EN_DESARROLLO = process.env.REACT_APP_MOSTRAR_MODULOS_EN_DESARROLLO === 'true';

interface IntegrationSuiteHomeProps {
  setPage: (page: string) => void;
}

interface ToolCardProps {
  title: string;
  desc: string;
  icon: React.ReactNode;
  // Solo las herramientas ya disponibles navegan; el resto sigue siendo una
  // tarjeta informativa.
  onClick?: () => void;
}

// 2026-08-20: `slate-*` (Tailwind, sin remapear a las variables de tema —
// ver tailwind.config.js) cambiado a `gray-*`/`zinc-*`, que sí responden al
// toggle claro/oscuro. `blue-*`/`cyan-*` son el acento de esta sección y
// quedan fijos a propósito, mismo criterio que el resto de la app.
const ToolCard: React.FC<ToolCardProps> = ({ title, desc, icon, onClick }) => (
  <div
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    className={`${onClick ? 'cursor-pointer ' : ''}group bg-zinc-900 border border-zinc-800 rounded-2xl p-8 transition-all duration-300 hover:border-cyan-500/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,180,216,0.1)]`}
  >
    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 bg-blue-500/10 border border-blue-500/20 group-hover:bg-cyan-500/10 group-hover:border-cyan-400/30 transition-all">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">{title}</h3>
    <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
  </div>
);

export default function IntegrationSuiteHome({ setPage }: IntegrationSuiteHomeProps) {
  return (
    <main className="min-h-screen bg-gray-900 text-white p-8">
      <div className="relative z-10 mb-8">
        <Breadcrumbs currentPage="btp-integration-suite" setPage={setPage} />
      </div>

      <header className="flex flex-col items-center text-center mb-16 mt-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg shadow-blue-900/40">
          <Workflow className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Integration Suite</h1>
        <p className="text-gray-400 max-w-2xl text-base font-light italic">
          Plataforma de integración iPaaS empresarial para conectar aplicaciones cloud y on-premise, diseñar iFlows, gestionar APIs y monitorear procesos.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {/* 2026-10-21: reemplaza a "Creación de Flujos" (era solo visual). Ver
            docs/plans/plan-repositorio-iflows-v1.md. */}
        <ToolCard
          title="Repositorio de iFlows"
          desc="Guarda, documenta y reutiliza tus iFlows exportados (.zip), con su documentación técnica, en tu repositorio personal o el de tu organización."
          icon={<FolderGit2 className="w-6 h-6 text-blue-400" />}
          onClick={() => setPage('btp-iflows')}
        />
        {MOSTRAR_MODULOS_EN_DESARROLLO && (
        <>
        <ToolCard
          title="Monitoreo"
          desc="Supervisión en tiempo real de ejecuciones de mensajes, logs de auditoría, estado del sistema y alertas."
          icon={<Activity className="w-6 h-6 text-blue-400" />}
        />
        <ToolCard
          title="Administración"
          desc="Gestión de artefactos de seguridad, keystores, credenciales, recursos del runtime y parámetros del tenant."
          icon={<Sliders className="w-6 h-6 text-blue-400" />}
        />
        </>
        )}

        <div className="flex flex-col items-center justify-center text-center p-8 rounded-2xl border-2 border-dashed border-gray-600 text-gray-500 min-h-[180px]">
          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mb-3">
            <Plus className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-sm">Más herramientas próximamente</p>
        </div>
      </div>
    </main>
  );
}
